import { pastelColors, tagIcons } from './constants';
import { FaTag } from "react-icons/fa6";

// Utility to truncate text to 100 chars
export function truncateText(text, maxLength = 100) {
  if (!text) return '';
  return text.length > maxLength ? text.slice(0, maxLength) + '...' : text;
}

// Function to generate a darker text color based on the background color
export const getDarkerTextColor = (hexColor) => {
  // Remove the # if present
  const hex = hexColor.replace('#', '');
  
  // Parse RGB values
  const r = parseInt(hex.substr(0, 2), 16);
  const g = parseInt(hex.substr(2, 2), 16);
  const b = parseInt(hex.substr(4, 2), 16);
  
  // Generate darker versions (reduce each component by about 40-60%)
  const darkerR = Math.max(0, Math.floor(r * 0.4));
  const darkerG = Math.max(0, Math.floor(g * 0.4));
  const darkerB = Math.max(0, Math.floor(b * 0.4));
  
  // Convert back to hex
  const toHex = (n) => n.toString(16).padStart(2, '0');
  return `#${toHex(darkerR)}${toHex(darkerG)}${toHex(darkerB)}`;
};

// Simple hash function to consistently assign colors based on user ID
export const getUserColor = (userId) => {
  if (!userId) return pastelColors[0];
  
  let hash = 0;
  for (let i = 0; i < userId.length; i++) {
    const char = userId.charCodeAt(i);
    hash = ((hash << 5) - hash) + char;
    hash = hash & hash; // Convert to 32-bit integer
  }
  
  return pastelColors[Math.abs(hash) % pastelColors.length];
};

// Function to get icon component by id
export const getTagIcon = (iconId) => {
  const iconData = tagIcons.find(icon => icon.id === iconId);
  return iconData ? iconData.icon : FaTag;
};

export const formatFileSize = (bytes) => {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return parseFloat((bytes / Math.pow(k, i)).toFixed(2)) + ' ' + sizes[i];
};

export const getStatusColor = (status) => {
  switch (status) {
    case 'completed': return 'text-green-600 bg-green-100 dark:text-green-400 dark:bg-green-900/20';
    case 'processing': return 'text-yellow-600 bg-yellow-100 dark:text-yellow-400 dark:bg-yellow-900/20';
    case 'failed': return 'text-red-600 bg-red-100 dark:text-red-400 dark:bg-red-900/20';
    case 'partial': return 'text-orange-600 bg-orange-100 dark:text-orange-400 dark:bg-orange-900/20';
    default: return 'text-gray-600 bg-gray-100 dark:text-gray-400 dark:bg-gray-900/20';
  }
};

export const getCategoryIcon = (category) => {
  switch (category) {
    case 'requirements': return '📋';
    case 'design': return '🎨';
    case 'technical': return '⚙️';
    case 'business': return '📊';
    case 'research': return '🔬';
    default: return '📄';
  }
};

export const formatTimeAgo = (dateString) => {
  const date = new Date(dateString);
  const now = new Date();
  const diffInMs = now - date;
  const diffInMinutes = diffInMs / (1000 * 60);
  const diffInHours = diffInMinutes / 60;
  const diffInDays = diffInHours / 24;

  if (diffInMinutes < 1) {
    return 'Just now';
  } else if (diffInMinutes < 60) {
    return `${Math.floor(diffInMinutes)} min ago`;
  } else if (diffInHours < 24) {
    return `${Math.floor(diffInHours)}h ago`;
  } else if (diffInDays < 7) {
    return `${Math.floor(diffInDays)}d ago`;
  } else {
    return date.toLocaleDateString();
  }
};

// Get current user ID from token
export const getCurrentUserId = () => {
  try {
    const token = localStorage.getItem('token');
    if (token) {
      const payload = JSON.parse(atob(token.split('.')[1]));
      return payload.userId || payload.id;
    }
  } catch (error) {
    console.error('Error decoding token:', error);
  }
  return null;
};

// Get member initials (same logic as CreateProjectModal)
export const getMemberInitials = (member) => {
  if (member?.firstName && member?.lastName) {
    return `${member.firstName.charAt(0)}${member.lastName.charAt(0)}`.toUpperCase();
  } else if (member?.name) {
    const nameParts = member.name.trim().split(' ');
    if (nameParts.length >= 2) {
      return `${nameParts[0].charAt(0)}${nameParts[nameParts.length - 1].charAt(0)}`.toUpperCase();
    } else {
      return nameParts[0].charAt(0).toUpperCase();
    }
  }
  return null; // Will fall back to placeholder
}; 